
import { UserViewModel } from '../ViewModels';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';

import { UserHeader } from '../user-config';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useUser } from '@/OneLiners';
import { useUserEnricher } from './useUserEnricher';
import { UserModel } from '../UserModel';

/**
 * useUserTableConfig hook
 *
 * Domain-specific layout composition hook for User entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate User domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for User table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<UserViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<UserViewModel>
 */
export function useUserTableConfig(props: DomainHookProps): DomainTableConfig<UserViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.User;

    const { UserEnums } = useAppEnum();

    const userHook = useUser();

    /**
     * Enrichment layer
     * Responsible for computing derived User fields and resolving related entities.
     */
    const { enricherResponse, getStrictEnrichedUser, } = useUserEnricher();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            userHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error:
            userHook.stateResponse.error
            ?? enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: userHook,

        /**
         * Header configuration for Property table.
         */
        headers: UserHeader.build(UserEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (user: UserModel, isDisplaying: boolean) =>
            new UserViewModel({
                user: getStrictEnrichedUser(user),
                viewEnums: UserEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}